/**
 * Utility functions for POS serial number management, validation, and deduplication.
 */

/**
 * Normalizes a serial number string or object into a clean, trimmed string.
 * @param {string|object} s
 * @returns {string}
 */
export function normalizeSerial(s) {
  if (!s) return "";
  if (typeof s === "string") {
    const trimmed = s.trim();
    if (trimmed === "null" || trimmed === "undefined") return "";
    return trimmed;
  }
  if (typeof s === "object") {
    const candidate =
      s.serial_number ||
      s.number ||
      s.serial ||
      s.serialNumber ||
      s.code ||
      "";
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed === "null" || trimmed === "undefined") return "";
      return trimmed;
    }
    return String(candidate || "").trim();
  }
  return String(s).trim();
}

/**
 * Extracts all unique serial numbers from a cart item.
 * Supports:
 * - item.serial_numbers (Array, JSON string, or plain string)
 * - item.serial_number (string or object)
 * - item.inventory_serial (object with serial_number or string)
 * - item.inventory_serials (Array, object, or JSON string)
 *
 * @param {object} item - Cart item object
 * @returns {string[]} List of unique serial numbers found on this item
 */
export function extractItemSerials(item) {
  if (!item || typeof item !== "object") return [];

  const serials = [];

  const addSerial = (val) => {
    const normalized = normalizeSerial(val);
    if (normalized) {
      serials.push(normalized);
    }
  };

  // 1. Process serial_numbers
  if (item.serial_numbers !== undefined && item.serial_numbers !== null) {
    if (Array.isArray(item.serial_numbers)) {
      item.serial_numbers.forEach(addSerial);
    } else if (typeof item.serial_numbers === "string") {
      const trimmed = item.serial_numbers.trim();
      if (trimmed && trimmed !== "null" && trimmed !== "undefined") {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            parsed.forEach(addSerial);
          } else if (parsed && typeof parsed === "object") {
            addSerial(parsed);
          } else if (typeof parsed === "string") {
            addSerial(parsed);
          } else {
            addSerial(trimmed);
          }
        } catch {
          // If not valid JSON, treat as raw serial string
          addSerial(trimmed);
        }
      }
    } else if (typeof item.serial_numbers === "object") {
      addSerial(item.serial_numbers);
    }
  }

  // 2. If no serials found yet, check inventory_serials
  if (serials.length === 0 && item.inventory_serials) {
    if (Array.isArray(item.inventory_serials)) {
      item.inventory_serials.forEach(addSerial);
    } else if (typeof item.inventory_serials === "string") {
      try {
        const parsed = JSON.parse(item.inventory_serials);
        if (Array.isArray(parsed)) {
          parsed.forEach(addSerial);
        } else {
          addSerial(parsed);
        }
      } catch {
        addSerial(item.inventory_serials);
      }
    } else {
      addSerial(item.inventory_serials);
    }
  }

  // 3. If no serials found yet, check serial_number
  if (serials.length === 0 && item.serial_number) {
    addSerial(item.serial_number);
  }

  // 4. If no serials found yet, check inventory_serial
  if (serials.length === 0 && item.inventory_serial) {
    addSerial(item.inventory_serial);
  }

  return serials;
}

/**
 * Returns all serial numbers present across all cart items.
 * Can optionally exclude a specific item ID (e.g. when updating/editing an existing item).
 *
 * @param {Array<object>} cartProducts - List of items in the cart
 * @param {string|number|null} excludeItemId - Item ID to exclude from the lookup
 * @returns {string[]} List of all serial numbers in the cart
 */
export function getAllCartSerials(cartProducts = [], excludeItemId = null) {
  if (!Array.isArray(cartProducts)) return [];
  const serials = [];

  for (const item of cartProducts) {
    if (!item) continue;
    if (
      excludeItemId &&
      (item.id === excludeItemId ||
        (item._tempId && item._tempId === excludeItemId))
    ) {
      continue;
    }
    const itemSerials = extractItemSerials(item);
    serials.push(...itemSerials);
  }

  return serials;
}

/**
 * Checks whether a given serial number is already present in the cart.
 * Comparison is case-insensitive and trims whitespace.
 *
 * @param {Array<object>} cartProducts
 * @param {string|object} serial
 * @param {string|number|null} excludeItemId
 * @returns {boolean}
 */
export function isSerialInCart(cartProducts = [], serial, excludeItemId = null) {
  const normalized = normalizeSerial(serial).toLowerCase();
  if (!normalized) return false;

  const existingSerials = getAllCartSerials(cartProducts, excludeItemId);
  return existingSerials.some((s) => s.toLowerCase() === normalized);
}

/**
 * Finds all duplicate serial numbers across cart items.
 * Returns an array of duplicate serial strings.
 *
 * @param {Array<object>} cartProducts
 * @returns {string[]} List of duplicate serial numbers
 */
export function findDuplicateSerialsInCart(cartProducts = []) {
  if (!Array.isArray(cartProducts)) return [];

  const seen = new Set();
  const duplicates = new Set();

  for (const item of cartProducts) {
    if (!item) continue;
    const itemSerials = extractItemSerials(item);
    for (const sn of itemSerials) {
      const lower = sn.toLowerCase();
      if (seen.has(lower)) {
        duplicates.add(sn);
      } else {
        seen.add(lower);
      }
    }
  }

  return Array.from(duplicates);
}

/**
 * Validates cart products for any duplicate serial numbers.
 *
 * @param {Array<object>} cartProducts
 * @returns {{ isValid: boolean, duplicates: string[] }}
 */
export function validateCartSerials(cartProducts = []) {
  const duplicates = findDuplicateSerialsInCart(cartProducts);
  return {
    isValid: duplicates.length === 0,
    duplicates,
  };
}

/**
 * Filters a product's available serial numbers against the cart.
 * Excludes any serial numbers that are already in the cart.
 * Also deduplicates entries in productSerials itself.
 *
 * @param {Array<object|string>} productSerials - Raw serials from product object
 * @param {Array<object>} cartProducts - Current items in the cart
 * @param {string|number|null} excludeItemId - Item ID to exclude from cart check
 * @returns {Array<object|string>} Filtered serial objects/strings
 */
export function filterAvailableProductSerials(
  productSerials = [],
  cartProducts = [],
  excludeItemId = null,
) {
  if (!Array.isArray(productSerials)) return [];

  const cartSerialsLower = getAllCartSerials(cartProducts, excludeItemId).map(
    (s) => s.toLowerCase(),
  );

  const seenInProduct = new Set();
  const available = [];

  for (const item of productSerials) {
    const sn = normalizeSerial(item);
    if (!sn) continue;
    const lower = sn.toLowerCase();

    // Skip duplicates within product's own serial list
    if (seenInProduct.has(lower)) continue;
    seenInProduct.add(lower);

    // Skip serials already present in cart
    if (cartSerialsLower.includes(lower)) continue;

    available.push(item);
  }

  return available;
}
